import './RecommendationBundle.scss';

import { registryComponent } from '@boost-sd/components-registry/registry';
import { getQueryParamByKey } from '@boost-sd/core-js/history';
import Button from '@components/Button';
import Dropdown from '@components/Dropdown';
import FormatCurrency from '@components/FormatCurrency';
import ProductImage from '@components/ProductImage';
import ProductLink from '@components/ProductLink';
import useGeneralSettings from '@hooks/useGeneralSettings';
import useTranslation from '@hooks/useTranslation';
import type { AddToCartState, CartData } from '@providers/CartProvider';
import type { ProductItem, ProductItemVariant } from '@providers/ProductProvider';
import { createClsNameMap, mapModifiers, mergeModifiers } from '@utils';
import classnames from '@utils/classnames';
import { Fragment, useEffect, useMemo, useState } from 'react';

export const clsNameMap = createClsNameMap({
  elements: {
    images: createClsNameMap({
      modifiers: ['style1', 'style2'],
    }),
    left: createClsNameMap(),
    right: createClsNameMap(),
    'total-price': createClsNameMap(),
    'price-text': createClsNameMap(),
    'image-item': createClsNameMap(),
    checkbox: createClsNameMap(),
    'checkbox-input': createClsNameMap(),
    'item-info': createClsNameMap({ modifiers: ['style1', 'style2'] }),
    'item-info-content': createClsNameMap({ modifiers: ['blur'] }),
    'item-title': createClsNameMap(),
    'dropdown-wrapper': createClsNameMap({ modifiers: ['style1', 'style2'] }),
    'plus-icon-wrapper': createClsNameMap({ modifiers: ['style1', 'style2'] }),
  },
  modifiers: ['style1', 'style2'],
})('recommendation-bundle');

export type RecommendationBundleProps = {
  style?: 'style1' | 'style2';
  productList: Array<ProductItem>;
};

const plusIcon = (
  <svg width='16' height='16' viewBox='0 0 16 16' fill='none' xmlns='http://www.w3.org/2000/svg'>
    <path
      fillRule='evenodd'
      clipRule='evenodd'
      d='M15 7H9V1C9 0.448 8.553 0 8 0C7.447 0 7 0.448 7 1V7H1C0.447 7 0 7.448 0 8C0 8.552 0.447 9 1 9H7V15C7 15.552 7.447 16 8 16C8.553 16 9 15.552 9 15V9H15C15.553 9 16 8.552 16 8C16 7.448 15.553 7 15 7Z'
      fill='#5C5F62'
    />
  </svg>
);

type variantDataType = {
  id?: string;
  quantity: number;
  image?: string | null;
  price?: string;
  title?: string;
};

const RecommendationBundle = ({ style = 'style1', productList }: RecommendationBundleProps) => {
  const { t } = useTranslation();

  const [cartData, setCartData] = useState<CartData>();

  const [addToCartState, setAddToCartState] = useState<AddToCartState>({
    isAdding: false,
    success: false,
    fail: false,
  });

  const [selectedVariants, setSelectedVariants] = useState<Array<variantDataType>>([]);

  const checkDefaultOption = (item: ProductItemVariant) => {
    /** If no have any variant BE always return default variant with title is Default Title. This filter to hide default variant */
    return item.title !== 'Default Title';
  };

  const totalPrice = useMemo(() => {
    return selectedVariants
      .reduce((total, currentVariant) => {
        return currentVariant.quantity !== 0 && currentVariant.price
          ? (total += Number(currentVariant.price))
          : total;
      }, 0)
      .toFixed(2);
  }, [selectedVariants]);

  const handleATC = (e: React.MouseEvent<HTMLButtonElement>) => {
    const dataAddToCart = selectedVariants
      .filter(
        (item, index) =>
          !!item.id &&
          item.quantity !== 0 &&
          // filter variants that added all to cart
          (index === 0 ||
            cartData?.items.find((Item) => Item.id === item.id)?.quantity !==
              productList[index].variants.find((Item) => Item.id === item.id)?.inventory_quantity)
      )
      .map(
        (item) => ({ id: item.id, quantity: item.quantity } as { id: string; quantity: number })
      );
    setAddToCartState((prev) => ({ ...prev, isAdding: true }));
    window.dispatchEvent(
      new CustomEvent('boost-sd-add-multi-product-to-cart', {
        detail: {
          data: dataAddToCart,
          onSuccess: () => {
            setAddToCartState((prev) => ({ ...prev, isAdding: false, success: true }));
            setTimeout(() => {
              setAddToCartState((prev) => ({ fail: false, isAdding: false, success: false }));
            }, 350);
          },
          onFail: () => {
            setAddToCartState((prev) => ({ ...prev, fail: true, isAdding: false }));
            setTimeout(() => {
              setAddToCartState((prev) => ({ fail: false, isAdding: false, success: false }));
            }, 350);
          },
        },
      })
    );
  };

  const handleChangeCheckbox = (indexChange: number, selected: boolean) => {
    const newState = selectedVariants.map((item, index) => {
      return index !== indexChange ? item : { ...item, quantity: selected ? 1 : 0 };
    });
    setSelectedVariants(newState);
  };

  const handleSelectDropdown = (indexChange: number, selectedValue: string) => {
    const selectedVariant = productList[indexChange].variants.find(
      (item) => item.id === selectedValue
    );

    const newState = selectedVariants.map((item, index) => {
      return index !== indexChange
        ? item
        : {
            ...item,
            id: selectedVariant?.id,
            image: selectedVariant?.image,
            price: selectedVariant?.price,
            title: selectedVariant?.title,
          };
    });

    setSelectedVariants(newState);
  };

  const {
    generalSettings: { no_image_url, addCollectionToProductUrl: hasCollection },
  } = useGeneralSettings();

  const renderItemInfo = (item: ProductItem, index: number) => {
    if (selectedVariants.length <= 1) return;

    const isShowCheckbox = !!selectedVariants[index].id;

    return (
      <div key={item.id} className={mapModifiers(clsNameMap['item-info'], [style])}>
        <div
          className={mapModifiers(clsNameMap['item-info-content'], {
            blur: selectedVariants[index].quantity === 0,
          })}
        >
          <div className={clsNameMap.elm('item-title')}>
            {' '}
            {style === 'style1' && isShowCheckbox && (
              <div className={clsNameMap.elm('checkbox')}>
                <input
                  disabled={!selectedVariants[index].id}
                  className={clsNameMap.elm('checkbox-input')}
                  type='checkbox'
                  checked={selectedVariants[index].quantity !== 0}
                  onChange={(e) => handleChangeCheckbox(index, e.target.checked)}
                />
              </div>
            )}
            {index === 0 && <b>This item: </b>}
            <ProductLink
              data={item}
              hasCollection={hasCollection}
              disabled={
                index === 0 || !selectedVariants[index].id || selectedVariants[index].quantity === 0
              }
              variantId={selectedVariants[index].id}
            >
              {item.title}
            </ProductLink>
          </div>

          <div className={mapModifiers(clsNameMap['dropdown-wrapper'], [style])}>
            <Dropdown
              disabled={!selectedVariants[index].id || selectedVariants[index].quantity === 0}
              options={item.variants
                .filter((item) => item.available && checkDefaultOption(item))
                .map((item) => ({
                  value: item.id,
                  label: item.title,
                }))}
              defaultOption={
                index === 0 && !!selectedVariants[0].id
                  ? { value: selectedVariants[0].id, label: selectedVariants[0].title || '' }
                  : undefined
              }
              onSelect={(selectedValue) => handleSelectDropdown(index, selectedValue)}
              showWhenNoOption={false}
            />
          </div>
          {style === 'style2' && isShowCheckbox && (
            <div className={clsNameMap.elm('checkbox')}>
              <input
                disabled={!selectedVariants[index].id}
                className={clsNameMap.elm('checkbox-input')}
                type='checkbox'
                checked={selectedVariants[index].quantity !== 0}
                onChange={(e) => handleChangeCheckbox(index, e.target.checked)}
              />
            </div>
          )}
          {(selectedVariants[index].price || Number(selectedVariants[index].price) === 0) && (
            <FormatCurrency value={selectedVariants[index].price} />
          )}
        </div>
      </div>
    );
  };

  const buttonText = addToCartState?.isAdding
    ? t('productItem.atcAddingToCartBtnLabel')
    : addToCartState?.success
    ? t('productItem.atcAddedToCartBtnLabel')
    : addToCartState?.fail
    ? t('productItem.atcFailedToCartBtnLabel')
    : t('productItem.atcAvailableLabel');

  useEffect(() => {
    window.addEventListener('boost-sd-set-cart-data', (e) => {
      const { cartData } = e.detail;

      setCartData(cartData);
    });

    return () => {
      window.removeEventListener('boost-sd-set-cart-data', (e) => {
        const { cartData } = e.detail;

        setCartData(cartData);
      });
    };
  }, []);

  useEffect(() => {
    setSelectedVariants(
      productList.map((item, index) => {
        if (index === 0) {
          const selectedVariantIdMainProduct = getQueryParamByKey('variant');
          if (selectedVariantIdMainProduct) {
            const selectedVariantMainProduct = item.variants.find(
              (item) => item.id.toString() === selectedVariantIdMainProduct
            );

            if (selectedVariantMainProduct?.available) {
              return {
                id: selectedVariantMainProduct?.id,
                quantity: 1,
                image: selectedVariantMainProduct?.image,
                price: selectedVariantMainProduct?.price,
                title: selectedVariantMainProduct?.title,
              };
            }
          }
        }

        const firstVariantsAvailable = item.variants.find((item) => item.available);
        return {
          id: firstVariantsAvailable?.id,
          quantity: firstVariantsAvailable ? 1 : 0,
          image: firstVariantsAvailable?.image,
          price: item.variants.length > 1 ? firstVariantsAvailable?.price : item.variants[0]?.price,
          title: firstVariantsAvailable?.title,
        };
      })
    );
  }, [productList]);

  if (selectedVariants.length <= 1) return null;

  return (
    <div
      className={mergeModifiers(clsNameMap, [
        {
          [style as string]: true,
        },
      ])}
    >
      <div className={clsNameMap.elm('left')}>
        <div className={mapModifiers(clsNameMap.images, [style])}>
          {productList.map((item, index) => (
            <Fragment key={item.id}>
              <div className={clsNameMap.elm('image-item')}>
                <ProductLink
                  data={item}
                  hasCollection={hasCollection}
                  disabled={
                    index === 0 ||
                    !selectedVariants[index].id ||
                    selectedVariants[index].quantity === 0
                  }
                  variantId={selectedVariants[index].id}
                  className={classnames('product-link-image')}
                >
                  <ProductImage
                    productId={item.variant_id || item.id}
                    imageModifiers={{
                      'has-border':
                        !!selectedVariants[index].id &&
                        selectedVariants[index].quantity !== 0 &&
                        style === 'style1',
                      blur: !selectedVariants[index].id || selectedVariants[index].quantity === 0,
                    }}
                    images={
                      selectedVariants[index].image ||
                      (item.images_info?.length > 0 ? item.images_info[0].src : no_image_url)
                    }
                    aspectRatio={'full-box'}
                  />
                </ProductLink>
                {style === 'style2' && renderItemInfo(item, index)}
              </div>

              {index !== productList.length - 1 && (
                <div className={mapModifiers(clsNameMap['plus-icon-wrapper'], [style])}>
                  {plusIcon}
                </div>
              )}
            </Fragment>
          ))}
        </div>
        {style === 'style1' && productList.map(renderItemInfo)}
      </div>
      <div className={clsNameMap.elm('right')}>
        {selectedVariants.findIndex((item) => !!item.id && item.quantity !== 0) !== -1 && (
          <div className={clsNameMap.elm('total-price')}>
            Total price:{' '}
            <b className={clsNameMap.elm('price-text')}>
              <FormatCurrency value={totalPrice} />
            </b>
          </div>
        )}
        <Button
          onClick={handleATC}
          buttonModifiers={['primary', 'large', 'round', 'height-fit-content', 'full-width']}
          disabled={
            addToCartState?.isAdding ||
            selectedVariants.findIndex((item) => !!item.id && item.quantity !== 0) === -1
          }
          className={
            buttonText === t('productItem.atcAvailableLabel') ? classnames('btn-add-to-cart') : ''
          }
        >
          {buttonText}
        </Button>
      </div>
    </div>
  );
};

export default registryComponent('RecommendationBundle', RecommendationBundle);
